import type { ReactElement } from "react";
import type { ApiTitle } from "@lumi/data";

export interface DetailsTabProps {
  readonly title: ApiTitle;
}

export function DetailsTab({ title }: DetailsTabProps): ReactElement {
  // Collect all cast names
  const allCastNames: string[] = [];
  if (title.castData && title.castData.length > 0) {
    allCastNames.push(...title.castData.map((member) => member.name));
  }
  if (title.castNames && title.castNames.length > 0) {
    for (const name of title.castNames) {
      if (!allCastNames.includes(name)) {
        allCastNames.push(name);
      }
    }
  }
  const sortedCast = [...allCastNames].sort((a, b) => a.localeCompare(b));

  const crewData = title.crewData;

  return (
    <div className="px-4 md:px-8 lg:px-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-12 gap-6 md:gap-8 lg:gap-12 sm:h-full sm:overflow-hidden">
      {/* Column 1: Title & Description */}
      <div className="col-span-1 md:col-span-2 lg:col-span-2 lg:row-span-2 xl:col-span-7 xl:row-span-1 space-y-4 sm:overflow-y-auto pr-0 sm:pr-8 scrollbar-hide">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">{title.name}</h3>
        </div>
        <p className="text-sm text-neutral-300 leading-relaxed">
          {title.description || title.synopsis || "An incredible story waiting to unfold."}
        </p>
      </div>

      {/* Column 2: Cast */}
      <div className="space-y-4 sm:overflow-y-auto pr-0 sm:pr-4 col-span-1 lg:col-span-1 xl:col-span-2 scrollbar-hide">
        <h3 className="text-base sm:text-lg font-semibold text-white">Cast</h3>
        {sortedCast.length > 0 ? (
          <div className="space-y-2">
            {sortedCast.map((name) => (
              <div key={name} className="text-neutral-100 text-xs leading-3">
                {name}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-neutral-500 italic text-xs sm:text-sm">No cast information available</p>
        )}
        {title.productionCompanies && title.productionCompanies.length > 0 && (
          <div className="pt-4">
            <h3 className="text-base sm:text-lg font-semibold text-white pb-4">Production Companies</h3>
            <div className="space-y-2">
              {title.productionCompanies.map((company) => (
                <div key={company} className="text-neutral-100 text-xs leading-3">
                  {company}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Column 3: Crew */}
      <div className="space-y-4 sm:overflow-y-auto pr-0 sm:pr-4 col-span-1 xl:col-span-3 scrollbar-hide">
        <h3 className="text-base sm:text-lg font-semibold text-white">Crew</h3>
        {crewData && crewData.length > 0 ? (
          <div className="space-y-2">
            {crewData.map((member, idx) => (
              <div key={`${member.name}-${idx}`} className="text-xs leading-3">
                <span className="text-neutral-100 font-medium">{member.name}</span>
                <span className="text-neutral-500"> • {member.job}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-neutral-500 italic text-xs sm:text-sm">No crew information available</p>
        )}
      </div>
    </div>
  );
}
